import { isPlatformServer } from '@angular/common';
import {
    Directive,
    EmbeddedViewRef,
    inject,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    SecurityContext,
    SimpleChanges,
    TemplateRef,
    ɵunwrapSafeValue as unwrapSafeValue,
    ViewContainerRef
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ICmsDirectiveSanitationService, ISanitationEvent } from '@icms/core/content/directive/icms-directive.sanitation.service';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { ICmsCoreIntegrationDirectiveService } from '@icms/core/integration/icms-core-integration-directive.service';
import last from 'lodash-es/last';
import { Subject, Subscription } from 'rxjs';
/**
 * Syntax
 *
 * @example plain text
 * ```html
 * <span *iCms="'property' of someObject;"></span>
 * ```
 *
 * @example text with html
 * ```html
 * <span *iCms="'property' of someObject; content 'html'"></span>
 * ```
 * @example text with tag
 * ```html
 * <span *iCms="'property' of someObject; tag 'h1'"></span>
 * ```
 *
 * @example skip empty
 * ```html
 * <span *iCms="'property' of someObject; skipEmpty: true;"></span>
 * ```
 */
@Directive({
    // eslint-disable-next-line @angular-eslint/prefer-standalone
    standalone: false,
    selector: '[iCms]'
})
export class ICmsDirective implements OnInit, OnChanges, OnDestroy {
    /**
     * Trigggers update of all iCms bindings
     */
    static readonly triggerMassUpdate$ = new Subject<void>();

    /**
     * key of property to print
     */
    @Input()
    iCms!: string;

    /**
     * tag which will wrap content, no wrapping if not set
     */
    @Input()
    iCmsTag?: string;

    /**
     * object containing the property to print
     */
    @Input()
    iCmsOf?: unknown | undefined;

    /**
     * Content type
     * html - full html support including wysiwyg editor
     * basicHtml - basic html support, no wysiwyg editor
     */
    @Input()
    iCmsContent?: 'html' | 'basicHtml';

    @Input()
    iCmsContentClass?: string | string[];

    /**
     * if empty element should be removed completely tags are allowed
     */
    @Input()
    iCmsSkipEmpty = false;

    node?: HTMLElement;

    private lastText?: string;
    private lastContentClass?: string | string[];
    private lastTag?: string;
    private initiated = false;
    private subscriptions = new Subscription();
    private viewRef: EmbeddedViewRef<unknown> | null = null;

    private readonly platformId = inject(PLATFORM_ID);
    private readonly domSanitizer = inject(DomSanitizer);

    constructor(
        private iCmsCoreIntegrationDirectiveService: ICmsCoreIntegrationDirectiveService,
        private iCmsDataChangeService: ICmsDataChangeService,
        private viewContainer: ViewContainerRef,
        private templateRef: TemplateRef<unknown>,
        private renderer: Renderer2,
        private iCmsDirectiveSanitationService: ICmsDirectiveSanitationService,
        readonly injector: Injector
    ) {}

    protected get copy() {
        return this.iCmsOf?.[this.iCms];
    }

    ngOnInit() {
        this.initiated = true;
        this.update();

        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe(event => {
                if (
                    event.changeType === NICmsDataChange.EChangeType.change &&
                    event.changedObject === this.iCmsOf &&
                    last(event.valuePath) === this.iCms
                ) {
                    this.update();
                }
            })
        );
        this.subscriptions.add(
            ICmsDirective.triggerMassUpdate$.subscribe(() => {
                this.update();
            })
        );
    }

    ngOnChanges(_changes: SimpleChanges) {
        this.update();
    }

    ngOnDestroy() {
        this.unregister();
    }

    assureLastTextIs(copy: string | undefined) {
        this.lastText = copy;
    }

    update(force: boolean = false) {
        if (this.initiated) {
            const event: ISanitationEvent = {
                copy: this.copy || '',
                tag: this.iCmsTag,
                content: this.iCmsContent,
                contentClass: this.iCmsContentClass
            };
            this.iCmsDirectiveSanitationService.sanitation$.next(event);

            if (force || this.lastText !== event.copy || this.lastContentClass !== event.contentClass || this.lastTag !== event.tag) {
                if (event.copy || !this.iCmsSkipEmpty) {
                    this.createNode();

                    if (['html', 'basicHtml'].includes(this.iCmsContent!) && this.node) {
                        const copy = this.sanitizeHtml(event.copy);
                        let contentClass = '';

                        if (this.iCmsContentClass) {
                            contentClass += ' class="';
                            if (Array.isArray(this.iCmsContentClass)) {
                                contentClass += this.iCmsContentClass.join(' ');
                            } else {
                                contentClass += this.iCmsContentClass;
                            }
                            contentClass += '"';
                        }

                        this.renderer.setProperty(
                            this.node,
                            'innerHTML',
                            this.iCmsTag ? `<${this.iCmsTag}${contentClass} ngSkipHydration>${copy}</${this.iCmsTag}>` : copy
                        );
                    } else {
                        if (this.iCmsTag) {
                            const wrapper = this.renderer.createElement(this.iCmsTag);
                            this.renderer.setProperty(wrapper, 'innerText', event.copy);
                            this.addContentClass(wrapper);
                            this.renderer.setProperty(this.node, 'innerHTML', '');
                            this.renderer.appendChild(this.node, wrapper);
                        } else {
                            this.addContentClass(this.node);
                            this.renderer.setProperty(this.node, 'innerText', event.copy);
                        }
                    }

                    this.lastText = event.copy;
                    this.lastContentClass = event.contentClass;
                    this.lastTag = event.tag;
                } else {
                    this.removeNode();
                }
            }
        }
    }

    private sanitizeHtml(inputHtml: string): string {
        // Parse the HTML string into a DOM structure
        if (isPlatformServer(this.platformId)) {
            if (/(script|link|style)/.test(inputHtml)) {
                return unwrapSafeValue(this.domSanitizer.sanitize(SecurityContext.HTML, inputHtml) ?? '');
            }

            return unwrapSafeValue(this.domSanitizer.bypassSecurityTrustHtml(inputHtml));
        }

        let doc: HTMLElement | null = null;

        const parser = new DOMParser();
        doc = parser.parseFromString(inputHtml, 'text/html').body;

        if (!doc) {
            return '';
        }

        // Tags to remove
        ['style', 'script', 'html', 'body', 'head', 'link'].forEach(tag => {
            const elements = doc.querySelectorAll(tag);
            elements.forEach(el => el.remove());
        });

        // Remove JavaScript-related attributes (e.g., onload, onclick)
        const elements = doc.querySelectorAll('*');
        elements?.forEach(element => {
            Array.from(element.attributes).forEach(attr => {
                if (attr.name.startsWith('on')) {
                    element.removeAttribute(attr.name);
                }
            });
        });

        return doc.innerHTML;
    }

    private createNode() {
        if (!this.node) {
            this.viewContainer.clear();
            this.viewRef = this.viewContainer.createEmbeddedView(this.templateRef);
            this.node = this.viewRef.rootNodes[0] as HTMLElement;

            this.iCmsCoreIntegrationDirectiveService.registerEntry(this);
        }
        if (this.iCmsContent) {
            this.renderer.addClass(this.node, 'cms-' + this.iCmsContent);
        }
    }

    private removeNode() {
        if (this.node) {
            this.unregister();
            this.viewContainer.clear();
            this.viewRef = null;
            this.node = undefined;
        }
    }

    private unregister() {
        this.iCmsCoreIntegrationDirectiveService.unregisterEntry(this);
    }

    private addContentClass(node?: HTMLElement) {
        if (node && this.iCmsContentClass) {
            if (Array.isArray(this.iCmsContentClass)) {
                this.iCmsContentClass.forEach(cls => this.renderer.addClass(node, cls));
            } else {
                this.renderer.addClass(node, this.iCmsContentClass);
            }
        }
    }
}
